import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { Trophy, Users, Target, ChevronRight } from 'lucide-react';
import { SponsorBanner } from '@/components/SponsorBanner';

const TGA_LOGO = "https://customer-assets.emergentagent.com/job_853d16bb-77c9-4e1d-85ff-db52f6af924e/artifacts/lsv7codb_Copy%20of%20TGA%20pro%20tour%20logo%20transparent.png";

export const LandingPage = () => {
  const navigate = useNavigate();
  const { member } = useAuth();

  const features = [
    {
      icon: Trophy,
      title: "Tournament Scoring",
      description: "Track scores for 3, 6, or 9 hole tournaments with standard golf scoring"
    },
    {
      icon: Users,
      title: "Up to 100 Players",
      description: "Support tournaments from casual 4-player games to large 100-player events"
    },
    {
      icon: Target,
      title: "Live Leaderboard",
      description: "Real-time rankings showing who's leading the pack"
    }
  ];

  return (
    <div className="min-h-screen hero-bg">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img 
            src={TGA_LOGO} 
            alt="TGA Pro Tour" 
            className="h-12 object-contain"
            data-testid="nav-logo"
          />
          <div className="flex items-center gap-4">
            {member ? (
              <Button 
                onClick={() => navigate('/dashboard')}
                className="bg-primary hover:bg-primary/90 rounded-pill px-6 btn-hover-scale"
                data-testid="nav-dashboard-btn"
              >
                Dashboard
              </Button>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/auth')}
                  className="text-muted-foreground hover:text-white"
                  data-testid="nav-login-btn"
                >
                  Login
                </Button>
                <Button 
                  onClick={() => navigate('/auth')}
                  className="bg-primary hover:bg-primary/90 rounded-pill px-6 btn-hover-scale"
                  data-testid="nav-register-btn"
                >
                  Join Now
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="animate-fade-in">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading text-white mb-6 leading-tight">
                Table Golf
                <span className="block text-primary text-glow-purple">Pro Tour</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-8 max-w-lg">
                The official scoring app for Table Golf Association tournaments. 
                Track your scores, compete on the leaderboard, and become a champion.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate(member ? '/dashboard' : '/auth')}
                  className="bg-primary hover:bg-primary/90 rounded-pill px-8 py-6 text-lg btn-hover-scale glow-purple"
                  data-testid="hero-start-btn"
                >
                  {member ? 'Go to Dashboard' : 'Get Started'}
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                  className="border-white/20 hover:bg-white/5 rounded-pill px-8 py-6 text-lg"
                  data-testid="hero-learn-btn"
                >
                  Learn More
                </Button>
              </div>
            </div>

            {/* Right: Logo */}
            <div className="flex justify-center lg:justify-end animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <div className="logo-container">
                <img 
                  src={TGA_LOGO} 
                  alt="TGA Pro Tour Logo" 
                  className="w-full max-w-md drop-shadow-2xl"
                  data-testid="hero-logo"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sponsor Banner - Above Features */}
      <SponsorBanner variant="hero" className="py-16 bg-card/30" />

      {/* Features Section */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-heading text-center text-white mb-4">
            Everything You Need
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Simple, powerful scoring for your Table Golf tournaments
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="card-hover bg-card border border-white/10 rounded-xl p-8 animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`feature-card-${index}`}
              >
                <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-heading text-white mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Home Game Promo */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <a
            href="https://tablegolfassociation.com/shop/meeple-beach-pro-edition/"
            target="_blank"
            rel="noopener noreferrer"
            className="block group"
            data-testid="home-game-promo"
          >
            <div className="bg-card border border-secondary/30 hover:border-secondary/60 rounded-2xl p-8 sm:p-10 flex flex-col sm:flex-row items-center gap-6 transition-all group-hover:shadow-lg group-hover:shadow-secondary/5">
              <img
                src={TGA_LOGO}
                alt="TGA Pro Tour Home Game"
                className="w-24 h-24 sm:w-28 sm:h-28 object-contain shrink-0"
              />
              <div className="text-center sm:text-left">
                <h3 className="text-xl sm:text-2xl font-heading text-white mb-2">Buy the Home Version!</h3>
                <p className="text-muted-foreground text-sm sm:text-base mb-3">
                  Click here to buy the home version of the TGA, America's favorite tabletop golf game.
                </p>
                <p className="text-secondary font-semibold text-sm">
                  Use code <span className="bg-secondary/20 px-2 py-0.5 rounded font-mono">TGAprotour</span> to save $10
                </p>
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-primary/20 to-secondary/10 border border-white/10 rounded-2xl p-12">
            <h2 className="text-3xl sm:text-4xl font-heading text-white mb-4">
              Ready to Play?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
              Join the Table Golf Association today and start tracking your tournament scores.
            </p>
            <Button 
              size="lg"
              onClick={() => navigate('/auth')}
              className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-pill px-10 py-6 text-lg btn-hover-scale glow-green"
              data-testid="cta-join-btn"
            >
              Join the TGA
            </Button>
            <p className="text-muted-foreground text-sm mt-4">
              Own a venue?{' '}
              <a href="/venues/register" className="text-primary hover:underline" data-testid="venue-register-link">Register your venue</a>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img 
            src={TGA_LOGO} 
            alt="TGA Pro Tour" 
            className="h-8 object-contain opacity-60"
          />
          <p className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} Table Golf Association. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};
